/**
 * TrapEntity - Placed trap that arms after a delay and triggers on enemy proximity
 * Has arm time, trigger radius, and explosion damage
 */

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { ElementType, Vector2 } from '@shared/types';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { ELEMENT_INFO } from '@shared/skills/SkillDeliveryTypes';

export type TrapState = 'arming' | 'armed' | 'triggered' | 'exploded';

export interface TrapEntityConfig {
  skill: CustomSkillV2;
  position: Vector2;
  ownerId: string;
  baseDamage: number;  // Player's base attack damage
  getDamage?: () => number; // Live damage callback for stat scaling
}

export class TrapEntity {
  public readonly id: string;
  public position: Vector2;
  public ownerId: string;
  public isAlive: boolean = true;

  private state: TrapState = 'arming';
  private armTimer: number;
  private armTime: number;
  private triggerRadius: number;
  private explosionRadius: number;
  private damage: number;
  private damageMultiplier: number;
  private getDamageCallback: (() => number) | null;
  private element: ElementType;
  private color: string;
  private explosionTimer: number = 0;
  private explosionDuration: number = 0.3;

  // Trap trigger type
  private trapTrigger: 'proximity' | 'timed';
  private timedDetonateTimer: number = 0;

  // Visual
  private pulseTime: number = 0;

  constructor(config: TrapEntityConfig) {
    this.id = `trap_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    this.position = { ...config.position };
    this.ownerId = config.ownerId;
    this.element = config.skill.element;

    // Calculate stats from skill
    this.armTime = SkillCreatorV2.getArmTime(config.skill);
    this.armTimer = this.armTime;
    this.triggerRadius = SkillCreatorV2.getEffectiveRadius(config.skill);
    this.explosionRadius = this.triggerRadius * 1.5;
    this.damageMultiplier = 1.0; // V2 uses flat damage directly
    this.getDamageCallback = config.getDamage || null;
    this.damage = SkillCreatorV2.getEffectiveDamage(config.skill);

    // Read trap trigger choice
    this.trapTrigger = config.skill.trapTrigger || 'proximity';
    // For timed traps, use armTime as the detonation timer
    this.timedDetonateTimer = this.armTime;

    // Get color from element
    this.color = ELEMENT_INFO[this.element].color;
  }

  getTrapTrigger(): 'proximity' | 'timed' {
    return this.trapTrigger;
  }

  getState(): TrapState {
    return this.state;
  }

  getDamage(): number {
    // Use live player stats if callback available
    if (this.getDamageCallback) {
      return Math.floor(this.getDamageCallback() * this.damageMultiplier);
    }
    return this.damage;
  }

  getElement(): ElementType {
    return this.element;
  }

  getExplosionRadius(): number {
    return this.explosionRadius;
  }

  isArmed(): boolean {
    return this.state === 'armed';
  }

  /**
   * Check if an entity triggers the trap
   * @param entityRadius - Hitbox radius of the entity for more accurate collision
   */
  checkTrigger(entityX: number, entityY: number, entityRadius: number = 0): boolean {
    if (this.state !== 'armed') return false;

    const dx = entityX - this.position.x;
    const dy = entityY - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Entity triggers trap if distance minus entity radius is within trigger radius
    if (distance <= this.triggerRadius + entityRadius) {
      this.trigger({ x: entityX, y: entityY });
      return true;
    }

    return false;
  }

  /**
   * Trigger the trap
   */
  trigger(_targetPos: Vector2): void {
    if (this.state !== 'armed') return;
    this.state = 'triggered';
    // Target position could be used for directional effects in the future
  }

  update(deltaTime: number): void {
    this.pulseTime += deltaTime;

    switch (this.state) {
      case 'arming':
        this.armTimer -= deltaTime;
        if (this.armTimer <= 0) {
          if (this.trapTrigger === 'timed') {
            // Timed traps go directly to armed and start counting down
            this.state = 'armed';
          } else {
            this.state = 'armed';
          }
        }
        break;

      case 'armed':
        // Timed trigger: auto-detonate after armTime seconds
        if (this.trapTrigger === 'timed') {
          this.timedDetonateTimer -= deltaTime;
          if (this.timedDetonateTimer <= 0) {
            this.trigger({ x: this.position.x, y: this.position.y });
          }
        }
        break;

      case 'triggered':
        // Instant explosion after trigger
        this.state = 'exploded';
        this.explosionTimer = this.explosionDuration;
        break;

      case 'exploded':
        this.explosionTimer -= deltaTime;
        if (this.explosionTimer <= 0) {
          this.isAlive = false;
        }
        break;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive) return;

    const ctx = renderer.context;
    const screenPos = camera.worldToScreen(this.position.x, this.position.y);
    const z = camera.zoom;

    ctx.save();

    switch (this.state) {
      case 'arming':
        this.renderArming(ctx, screenPos, z);
        break;
      case 'armed':
        this.renderArmed(ctx, screenPos, z);
        break;
      case 'triggered':
      case 'exploded':
        this.renderExplosion(ctx, screenPos, z);
        break;
    }

    ctx.restore();
  }

  private renderArming(ctx: CanvasRenderingContext2D, pos: Vector2, z: number): void {
    const progress = 1 - (this.armTimer / this.armTime);
    const baseSize = 15 * z;

    // Background ring (track)
    ctx.strokeStyle = '#404040';
    ctx.lineWidth = 4 * z;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, baseSize, 0, Math.PI * 2);
    ctx.stroke();

    // Progress ring with gradient-like effect
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 3 * z;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, baseSize, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
    ctx.stroke();

    // Progress glow at the tip
    if (progress > 0.05) {
      const tipAngle = -Math.PI / 2 + progress * Math.PI * 2;
      const tipX = pos.x + Math.cos(tipAngle) * baseSize;
      const tipY = pos.y + Math.sin(tipAngle) * baseSize;
      ctx.fillStyle = this.color;
      ctx.shadowColor = this.color;
      ctx.shadowBlur = 8 * z;
      ctx.beginPath();
      ctx.arc(tipX, tipY, 3 * z, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // Center dot (pulsing)
    const pulse = 0.5 + Math.sin(this.pulseTime * 8) * 0.5;
    ctx.fillStyle = this.color + Math.floor(pulse * 255).toString(16).padStart(2, '0');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 4 * z, 0, Math.PI * 2);
    ctx.fill();

    // Countdown text
    const remaining = Math.max(0, this.armTimer);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${12 * z}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(remaining.toFixed(1), pos.x, pos.y + baseSize + 12 * z);

    // Arming label
    ctx.fillStyle = '#ffffff80';
    ctx.font = `${9 * z}px Arial`;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('KURMA', pos.x, pos.y - baseSize - 5 * z);

    // Percentage arc text
    ctx.fillStyle = this.color;
    ctx.font = `bold ${10 * z}px Arial`;
    ctx.fillText(`${Math.floor(progress * 100)}%`, pos.x, pos.y - baseSize - 16 * z);
  }

  private renderArmed(ctx: CanvasRenderingContext2D, pos: Vector2, z: number): void {
    const pulse = Math.sin(this.pulseTime * 4) * 0.2 + 1;
    const baseSize = 15 * z * pulse;

    // Trigger radius indicator (faint)
    ctx.strokeStyle = this.color + '30';
    ctx.lineWidth = 1;
    ctx.setLineDash([4 * z, 4 * z]);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, this.triggerRadius * z, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Main trap body
    ctx.fillStyle = '#1a1510';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, baseSize, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2 * z;
    ctx.stroke();

    // Danger pattern (spikes)
    const spikes = 8;
    ctx.fillStyle = this.color;
    for (let i = 0; i < spikes; i++) {
      const angle = (i / spikes) * Math.PI * 2 + this.pulseTime * 0.5;
      const innerR = baseSize * 0.6;
      const outerR = baseSize * 1.2;

      ctx.beginPath();
      ctx.moveTo(pos.x + Math.cos(angle) * innerR, pos.y + Math.sin(angle) * innerR);
      ctx.lineTo(pos.x + Math.cos(angle + 0.15) * outerR, pos.y + Math.sin(angle + 0.15) * outerR);
      ctx.lineTo(pos.x + Math.cos(angle - 0.15) * outerR, pos.y + Math.sin(angle - 0.15) * outerR);
      ctx.closePath();
      ctx.fill();
    }

    // Center glow
    const glowGrad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, baseSize * 0.5);
    glowGrad.addColorStop(0, this.color);
    glowGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, baseSize * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderExplosion(ctx: CanvasRenderingContext2D, pos: Vector2, z: number): void {
    const progress = 1 - (this.explosionTimer / this.explosionDuration);
    const maxRadius = this.explosionRadius * z;
    const currentRadius = maxRadius * Math.min(1, progress * 1.5);
    const alpha = 1 - progress;

    // Explosion circle
    const explosionGrad = ctx.createRadialGradient(
      pos.x, pos.y, 0,
      pos.x, pos.y, currentRadius
    );
    explosionGrad.addColorStop(0, this.color);
    explosionGrad.addColorStop(0.3, this.color + Math.floor(alpha * 200).toString(16).padStart(2, '0'));
    explosionGrad.addColorStop(0.7, this.color + Math.floor(alpha * 100).toString(16).padStart(2, '0'));
    explosionGrad.addColorStop(1, 'transparent');

    ctx.fillStyle = explosionGrad;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, currentRadius, 0, Math.PI * 2);
    ctx.fill();

    // Shockwave ring
    ctx.strokeStyle = '#ffffff' + Math.floor(alpha * 180).toString(16).padStart(2, '0');
    ctx.lineWidth = (3 - progress * 2) * z;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, currentRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Debris particles
    const particleCount = 12;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const dist = currentRadius * (0.5 + Math.random() * 0.5);
      const px = pos.x + Math.cos(angle) * dist;
      const py = pos.y + Math.sin(angle) * dist - progress * 30 * z;
      const size = (4 - progress * 3) * z;

      ctx.fillStyle = this.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Factory function to create trap entity
 */
export function createTrapEntity(config: TrapEntityConfig): TrapEntity {
  return new TrapEntity(config);
}
